import { SettingsService } from "@/services/";
import { constants } from "@/const";
import { mapGetters } from "vuex";
import { formatDateTime } from "@/components/mixins";

// Whitelist sanitizer: anything not listed here is stripped.
const ALLOWED_TAGS = new Set([
    "A", "B", "STRONG", "I", "EM", "U", "S", "BR", "HR", "P", "DIV", "SPAN", "SMALL", "SUB", "SUP",
    "UL", "OL", "LI", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "CODE",
]);
// Removed together with their content instead of being unwrapped.
const DROPPED_TAGS = new Set([
    "SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "SVG", "MATH", "TEMPLATE", "NOSCRIPT", "TEXTAREA", "TITLE",
]);
const ALLOWED_STYLES = [
    "color", "background-color", "font-weight", "font-style", "font-size", "text-decoration", "text-align",
];
const SAFE_STYLE_VALUE = /^[#\w\s.,%()-]+$/;
const BLOCKED_STYLE_VALUE = /url|expression|var\(|attr\(|image/i;
const SAFE_HREF = /^(https?:|mailto:|tel:)/i;
const HTML_TAG = /<\/?[a-z][^>]*>/i;


export default {
    name: "MessageBanner",
    mixins: [formatDateTime],
    props: {
        sideCartOpen: { type: Boolean, default: false },
        sideCartToggleVisible: { type: Boolean, default: false },
    },
    data() {
        return {
            header: '',
            message: '',
            start: null,
            end: null,
            costCenters: null,
            leftInset: 0,
            rightInset: 0,
            toggleInset: 0,
            rafId: 0,
        };
    },
    computed: {
        ...mapGetters(["McfCostCenterId"]),
        bannerStyle() {
            if (!this.sideCartOpen) {
                return this.toggleInset > 0 ? { paddingRight: `${this.toggleInset}px` } : {};
            }
            return { width: "auto", marginLeft: `${this.leftInset}px`, marginRight: `${this.rightInset}px` };
        },
        shouldShowBanner() {
            return this.isCurrentTimeInRange(this.start, this.end) && this.isCostCenterIncluded(this.costCenters)
        },
        isHtmlMessage() {
            return HTML_TAG.test(this.message || "");
        },
        messageHtml() {
            return this.isHtmlMessage ? this.sanitizeHtml(this.message) : this.toSafeHtml(this.message);
        }
    },
    watch: {
        sideCartOpen() {
            this.trackInsets();
        },
        sideCartToggleVisible() {
            this.trackInsets();
        },
        // The banner element only exists once settings have loaded.
        shouldShowBanner() {
            this.$nextTick(this.trackInsets);
        },
    },
    mounted() {
        window.addEventListener("resize", this.updateInsets);
        this.trackInsets();
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.updateInsets);
        cancelAnimationFrame(this.rafId);
    },
    created() {
        SettingsService.getAllSettings().then((response) => {
            var settings = response;
            var settingKeys = constants.MLA_BANNER_SETTINGS;
            this.start = _.find(settings, (x) => x.Key == settingKeys.START_DATE)?.Value;
            this.end = _.find(settings, (x) => x.Key == settingKeys.END_DATE)?.Value;
            this.costCenters = _.find(settings, (x) => x.Key == settingKeys.COST_CENTERS)?.Value;

            this.header = _.find(settings, (x) => x.Key == settingKeys.HEADER)?.Value;
            this.message = _.find(settings, (x) => x.Key == settingKeys.MESSAGE)?.Value;

        });
    },
    methods: {
        // Re-measure every frame while the side cart and page shift animate.
        trackInsets() {
            const end = Date.now() + 800;
            const step = () => {
                this.updateInsets();
                if (Date.now() < end) {
                    this.rafId = requestAnimationFrame(step);
                }
            };
            cancelAnimationFrame(this.rafId);
            step();
        },
        // Banner spans from the viewport's left edge to the side cart's left edge.
        updateInsets() {
            this.updateToggleInset();
            const parent = this.$el && this.$el.parentElement;
            const cart = document.querySelector(".side-cart");
            if (!this.sideCartOpen || !parent || !cart || getComputedStyle(cart).visibility === "hidden") {
                this.leftInset = 0;
                this.rightInset = 0;
                return;
            }
            const parentRect = parent.getBoundingClientRect();
            const cartRect = cart.getBoundingClientRect();
            this.leftInset = Math.max(0, -parentRect.left);
            this.rightInset = Math.max(0, parentRect.right - cartRect.left);
        },
        // With the side cart closed, pad the banner so its text stops before the fixed toggle button.
        updateToggleInset() {
            const banner = this.$el;
            const toggle = document.querySelector(".app__expand-sc");
            if (
                this.sideCartOpen ||
                !this.sideCartToggleVisible ||
                !banner ||
                banner.nodeType !== Node.ELEMENT_NODE ||
                !toggle ||
                getComputedStyle(toggle).visibility === "hidden"
            ) {
                this.toggleInset = 0;
                return;
            }
            const bannerRect = banner.getBoundingClientRect();
            const toggleRect = toggle.getBoundingClientRect();
            const overlapsVertically = toggleRect.bottom > bannerRect.top && toggleRect.top < bannerRect.bottom;
            if (!overlapsVertically) {
                this.toggleInset = 0;
                return;
            }
            const scrollbarWidth = banner.offsetWidth - banner.clientWidth;
            const gap = 12;
            this.toggleInset = Math.max(0, Math.ceil(bannerRect.right - toggleRect.left - scrollbarWidth + gap));
        },
        isCostCenterIncluded(csvString) {
            if (!csvString) {
                return false;
            }
            const costCenterIds = csvString.split(',').map(id => parseInt(id.trim(), 10));
            return costCenterIds.includes(this.McfCostCenterId);
        },
        sanitizeHtml(html) {
            // DOMParser documents are inert: no scripts run and no resources load while parsing.
            const doc = new DOMParser().parseFromString(String(html), "text/html");
            this.cleanNode(doc.body);
            return doc.body.innerHTML;
        },
        cleanNode(parent) {
            Array.from(parent.childNodes).forEach((node: any) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    return;
                }
                if (node.nodeType !== Node.ELEMENT_NODE || DROPPED_TAGS.has(node.tagName)) {
                    parent.removeChild(node);
                    return;
                }
                this.cleanNode(node);
                if (!ALLOWED_TAGS.has(node.tagName)) {
                    while (node.firstChild) {
                        parent.insertBefore(node.firstChild, node);
                    }
                    parent.removeChild(node);
                    return;
                }
                this.cleanAttributes(node);
            });
        },
        cleanAttributes(element) {
            Array.from(element.attributes).forEach((attr: any) => {
                const name = attr.name.toLowerCase();
                if (name === "style") {
                    const style = this.cleanStyle(attr.value);
                    style ? element.setAttribute("style", style) : element.removeAttribute("style");
                } else if (name === "href" && element.tagName === "A") {
                    // Strip whitespace/control characters that can hide a javascript: scheme.
                    // eslint-disable-next-line no-control-regex
                    const href = attr.value.replace(/[\u0000-\u0020]/g, "");
                    SAFE_HREF.test(href) ? element.setAttribute("href", href) : element.removeAttribute("href");
                } else if (!(name === "title" && element.tagName === "A")) {
                    element.removeAttribute(attr.name);
                }
            });
            if (element.tagName === "A") {
                element.setAttribute("target", "_blank");
                element.setAttribute("rel", "noopener noreferrer");
            }
        },
        cleanStyle(styleText) {
            return styleText
                .split(";")
                .map((declaration) => {
                    const separator = declaration.indexOf(":");
                    if (separator < 0) {
                        return "";
                    }
                    const property = declaration.slice(0, separator).trim().toLowerCase();
                    const value = declaration.slice(separator + 1).trim();
                    const isSafe = ALLOWED_STYLES.includes(property)
                        && SAFE_STYLE_VALUE.test(value)
                        && !BLOCKED_STYLE_VALUE.test(value);
                    return isSafe ? `${property}: ${value}` : "";
                })
                .filter(Boolean)
                .join("; ");
        },
        escapeHtml(text) {
            return text
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");
        },
        // Plain-text fallback: supports [text](url) and bare http(s) URLs; line breaks are preserved by CSS (pre-line).
        toSafeHtml(text) {
            if (!text) {
                return "";
            }
            const source = String(text);
            const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>"']+)/gi;
            let html = "";
            let lastIndex = 0;
            let match;
            while ((match = linkPattern.exec(source)) !== null) {
                html += this.escapeHtml(source.slice(lastIndex, match.index));
                let label = match[1];
                let url = match[2];
                let trailing = "";
                if (!url) {
                    const trimmed = match[3].match(/^(.*?)([.,;:!?)]*)$/);
                    url = trimmed[1];
                    trailing = trimmed[2];
                    label = url;
                }
                html += `<a href="${this.escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(label)}</a>${this.escapeHtml(trailing)}`;
                lastIndex = match.index + match[0].length;
            }
            return html + this.escapeHtml(source.slice(lastIndex));
        },
    },
};